import { GameDayModel } from '../../models/gameday-model';

class OrdersGroup {
  id: string;
  value: any;
  orders: GameDayModel.Order[];
}

class OrdersSegment<T extends any> {
  id: string;
  value: T;
  segments: OrdersSegment<T>[];
  groups: OrdersGroup[];
  orders: GameDayModel.Order[];
  maturities: GameDayModel.Maturity[];
}

interface SegmentGroupExtractor<T> {
  id?: (maturity: GameDayModel.Maturity | GameDayModel.Order) => string;
  value?: (
    maturity: GameDayModel.Maturity | GameDayModel.Order,
    instance?: any
  ) => T;
  mapper?: (input: any) => any;
  sortComparator?: (
    item1: OrdersSegment<T> | OrdersGroup,
    item2: OrdersSegment<T> | OrdersGroup
  ) => number;
}

function iterateGet<T>(
  segment: OrdersSegment<T>,
  maturities: GameDayModel.Maturity[],
  segmentExtractorsObject: SegmentGroupExtractor<T>,
  groupExtractor?: SegmentGroupExtractor<T>
): void {
  const segments = segment.segments;
  if (segments) {
    segments.forEach((innerSegment: OrdersSegment<T>) => {
      iterateGet(
        innerSegment,
        maturities,
        segmentExtractorsObject,
        groupExtractor
      );
    });
  } else {
    segment.segments = segmentOrders(
      segment.orders,
      segment.maturities,
      segmentExtractorsObject,
      groupExtractor
    );
  }
}

function getSegments<T>(
  orders: GameDayModel.Order[],
  maturities: GameDayModel.Maturity[],
  segmentExtractors: SegmentGroupExtractor<T>[],
  groupExtractor?: SegmentGroupExtractor<T>
): OrdersSegment<T>[] {
  return segmentExtractors.reduce(
    (
      segments: OrdersSegment<T>[],
      segmentExtractorsObject: SegmentGroupExtractor<T>
    ): OrdersSegment<T>[] => {
      if (segments.length > 0) {
        segments.forEach((segment: OrdersSegment<T>) => {
          iterateGet(
            segment,
            maturities,
            segmentExtractorsObject,
            groupExtractor
          );
        });
      } else {
        segments = segmentOrders(
          orders,
          maturities,
          segmentExtractorsObject,
          groupExtractor
        );
      }
      return segments;
    },
    []
  );
}

function segmentOrders<T>(
  orders: GameDayModel.Order[],
  segmentMaturities: GameDayModel.Maturity[],
  segmentExtractor: SegmentGroupExtractor<T>,
  groupExtractor: SegmentGroupExtractor<T> = {}
): OrdersSegment<T>[] {
  const segmentIdExtractor: any =
    segmentExtractor.id || ((order: GameDayModel.Order) => undefined);
  const segmentValueExtractor = segmentExtractor.value;
  const segmentMapper = segmentExtractor.mapper || ((segment) => segment);
  const segmentSortComparator = segmentExtractor.sortComparator;

  const groupIdExtractor: any =
    groupExtractor.id || ((order: GameDayModel.Order) => undefined);
  const groupValueExtractor = groupExtractor.value || (() => undefined);
  const groupMapper = groupExtractor.mapper || ((segment) => segment);
  const groupSortComparator = groupExtractor.sortComparator;

  const segmentsMap = {};

  for (const segmentMaturity of segmentMaturities) {
    const id: string = segmentIdExtractor(segmentMaturity);
    const segment: any = segmentsMap[id];
    const value: any = segmentValueExtractor
      ? segmentValueExtractor(segmentMaturity, segment)
      : undefined;

    let maturityOrders: GameDayModel.Order[] = orders.filter(
      (order) => order.maturityId === segmentMaturity.id
    );

    let maturities: GameDayModel.Maturity[] = [];
    let groupsMap = {};

    if (segment) {
      groupsMap = segment.groupsMap;
      maturities = segment.maturities;
    }

    setGroupOnMap(
      groupsMap,
      maturityOrders,
      groupIdExtractor,
      groupValueExtractor
    );
    maturities.push(segmentMaturity);
    maturityOrders = segment
      ? segment.orders.concat(maturityOrders)
      : maturityOrders;

    segmentsMap[id] = {
      id,
      value,
      orders: maturityOrders,
      maturities,
      groupsMap
    };
  }

  return convertMapsToArrays(
    segmentsMap,
    groupMapper,
    groupSortComparator,
    segmentMapper,
    segmentSortComparator
  );
}

function convertMapsToArrays<T>(
  map,
  groupMapper: (g: OrdersGroup) => OrdersGroup,
  groupSorter: (a: OrdersGroup, b: OrdersGroup) => number,
  segmentMapper: (s: OrdersSegment<T>) => OrdersSegment<T>,
  segmentSorter: (a: OrdersSegment<T>, b: OrdersSegment<T>) => number
): OrdersSegment<T>[] {
  return Object.keys(map)
    .map((segmentKey) => {
      const segment = map[segmentKey];
      segment.groups = Object.keys(segment.groupsMap)
        .map((groupKey) => groupMapper(segment.groupsMap[groupKey]))
        .sort(groupSorter);
      return segmentMapper(segment);
    })
    .sort(segmentSorter);
}

function setGroupOnMap(
  map,
  orders: GameDayModel.Order[],
  idExtractor: (order: GameDayModel.Order) => string,
  valueExtractor: (order: GameDayModel.Order, group: OrdersGroup) => any
) {
  for (const order of orders) {
    const groupId = idExtractor(order);
    const group: OrdersGroup = map[groupId];
    const groupValue = valueExtractor(order, group);

    if (group) {
      group.orders.push(order);
    } else {
      map[groupId] = {
        id: groupId,
        value: groupValue,
        orders: [order]
      };
    }
  }
}

function mapSegment<T>(
  parent: OrdersSegment<T>,
  current: OrdersSegment<T>
): OrdersSegment<T> {
  const id = `${parent.id}_${current.id}`;
  let value;
  if (typeof current.value === 'object' && typeof parent.value === 'object') {
    value = Object.assign({}, current.value, parent.value);
    return { ...current, id, value };
  }
  return { ...current, id };
}

function iterateFlatten<T>(
  iterationSegments: OrdersSegment<T>[],
  iterationSegment: OrdersSegment<T>,
  depth = 0,
  toDepth = Number.MAX_SAFE_INTEGER
): OrdersSegment<T>[] {
  let results;
  if (
    depth < toDepth &&
    iterationSegment.segments &&
    iterationSegment.segments[0] &&
    iterationSegment.segments[0].segments
  ) {
    results = iterationSegment.segments.reduce(
      (segments: OrdersSegment<T>[], segment: OrdersSegment<T>) =>
        segments.concat(iterateFlatten([], segment, depth + 1, toDepth)),
      []
    );
  } else if (iterationSegment.segments) {
    results = iterationSegment.segments;
  } else {
    return [iterationSegment];
  }
  results = results.map((result) => mapSegment(iterationSegment, result));
  return iterationSegments.concat(results);
}

function flattenTo<T>(
  ordersSegments: OrdersSegment<T>[],
  toLevel?: number
): OrdersSegment<T>[] {
  if (toLevel < 1) {
    return ordersSegments;
  }
  return ordersSegments.reduce(
    (segments: OrdersSegment<T>[], segment: OrdersSegment<T>) =>
      segments.concat(iterateFlatten([], segment, 1, toLevel)),
    []
  );
}

function flattenBeforeAfter<T>(
  ordersSegments: OrdersSegment<T>[],
  level: number
): OrdersSegment<T>[] {
  return flattenTo(ordersSegments, level).map((segment: OrdersSegment<T>) => {
    let segments = segment.segments;
    if (segments) {
      segments = flattenTo(segments, undefined)
        // mapper to generate uniq ID
        .map((innerSegment) => mapSegment(segment, innerSegment));
    }
    return { ...segment, segments };
  });
}

export {
  segmentOrders,
  getSegments,
  iterateGet,
  mapSegment,
  flattenTo,
  flattenBeforeAfter,
  SegmentGroupExtractor,
  OrdersSegment,
  OrdersGroup
};
